import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { doctorApi } from "@/api/doctor";
import { HospitalStaff, Appointment, VisitType } from "@/types/types";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import DoctorSlots from "@/components/DoctorSlots";
import { DoctorSearchableSelect } from "@/components/common/DoctorSearchableSelect";
import { LeadSourceSearchableSelect } from "@/components/common/LeadSourceSearchableSelect";
import { leadSourceNeedsDetails } from "@/lib/leadSources";
import { patientNeedsRegistrationSourceCapture } from "@/lib/patientRegistrationSource";
import { useBookingHospitals } from "@/hooks/useBookingHospitals";
import { useAuth } from "@/contexts/AuthContext";
import { appointmentApi } from "@/api/appointment";
import {
    formatIstTimeHHmm,
    istDateAndTimeToIso,
    toIstDateInputValue,
} from "@/utils/dateUtils";
import { canBackdateOpd as userCanBackdateOpd } from "@/lib/opdBackdateAccess";

type FormVisitType = 'OPD' | 'IPD' | 'EMERGENCY' | 'DAY_CARE' | 'FOLLOW_UP';

function visitTypeToForm(v: VisitType | string): FormVisitType {
    if (v === VisitType.ER || v === 'ER') return 'EMERGENCY';
    if (v === VisitType.FOLLOW_UP || v === 'FOLLOW_UP') return 'FOLLOW_UP';
    if (v === VisitType.DAY_CARE || v === 'DAY_CARE') return 'DAY_CARE';
    if (v === VisitType.IPD || v === 'IPD') return 'IPD';
    return 'OPD';
}

function formVisitTypeToApi(v: FormVisitType): string {
    return v === 'EMERGENCY' ? 'ER' : v;
}

interface UpdateAppointmentProps {
    appointment: Appointment;
    isOpen: boolean;
    onClose: () => void;
}

export default function UpdateAppointment({ appointment, isOpen, onClose }: UpdateAppointmentProps) {
    const { user, isChainAdmin } = useAuth();
    const canBackdateOpd = userCanBackdateOpd(user, isChainAdmin);
    const queryClient = useQueryClient();
    const {
        hospitals: bookingHospitals,
        needsHospitalPicker,
        defaultHospitalId,
    } = useBookingHospitals();

    const { data: fullAppointmentRes } = useQuery({
        queryKey: ["appointment", appointment.id],
        queryFn: async () => {
            const res = await appointmentApi.getAppointmentById(appointment.id);
            return (res.data as { data?: Appointment })?.data ?? appointment;
        },
        enabled: isOpen && !!appointment.id,
        staleTime: 0,
    });

    const activeAppointment = fullAppointmentRes ?? appointment;

    const hasClinicalLock = useMemo(
        () =>
            (activeAppointment.bills?.length ?? 0) > 0 ||
            !!activeAppointment.diagnosisRecord ||
            !!activeAppointment.billId,
        [activeAppointment]
    );

    const [selectedBookingHospitalId, setSelectedBookingHospitalId] = useState(activeAppointment.hospitalId);
    const [selectedDoctorId, setSelectedDoctorId] = useState(activeAppointment.doctorId);
    const [visitType, setVisitType] = useState<FormVisitType>(visitTypeToForm(activeAppointment.visitType));
    const [selectedDate, setSelectedDate] = useState(() =>
        toIstDateInputValue(activeAppointment.scheduledAt)
    );
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>(() =>
        formatIstTimeHHmm(activeAppointment.scheduledAt)
    );
    const [doctorOpdCharge, setDoctorOpdCharge] = useState(0);
    const [registrationSource, setRegistrationSource] = useState(
        activeAppointment.patient?.registrationSource || activeAppointment.registrationSource || ""
    );
    const [registrationSourceDetails, setRegistrationSourceDetails] = useState(
        activeAppointment.patient?.registrationSourceDetails || activeAppointment.registrationSourceDetails || ""
    );

    const effectiveBookingHospitalId =
        selectedBookingHospitalId ||
        activeAppointment.hospitalId ||
        defaultHospitalId ||
        user?.hospitalId ||
        "";

    const selectedBookingHospital = bookingHospitals.find(
        (h) => h.id === effectiveBookingHospitalId
    );

    const selectedPatient = activeAppointment.patient;
    const needsRegistrationSourceCapture = patientNeedsRegistrationSourceCapture(selectedPatient);
    const isFollowUp = visitType === 'FOLLOW_UP';

    useEffect(() => {
        if (!isOpen) return;
        setSelectedBookingHospitalId(activeAppointment.hospitalId);
        setSelectedDoctorId(activeAppointment.doctorId);
        setVisitType(visitTypeToForm(activeAppointment.visitType));
        setSelectedDate(toIstDateInputValue(activeAppointment.scheduledAt));
        setSelectedTimeSlot(formatIstTimeHHmm(activeAppointment.scheduledAt));
        setRegistrationSource(
            activeAppointment.patient?.registrationSource ||
                activeAppointment.registrationSource ||
                ""
        );
        setRegistrationSourceDetails(
            activeAppointment.patient?.registrationSourceDetails ||
                activeAppointment.registrationSourceDetails ||
                ""
        );
        const fee = activeAppointment.doctor?.opdCharge?.amount || 0;
        setDoctorOpdCharge(typeof fee === 'number' ? fee : 0);
    }, [isOpen, activeAppointment]);

    const { data: doctors } = useQuery<HospitalStaff[]>({
        queryKey: ["doctors", effectiveBookingHospitalId],
        queryFn: () => doctorApi.getDoctorsByHospital(effectiveBookingHospitalId || undefined),
        enabled: !!effectiveBookingHospitalId && isOpen,
    });

    useEffect(() => {
        if (!doctors?.length || !selectedDoctorId) return;
        if (!doctors.some((d) => d.id === selectedDoctorId)) {
            setSelectedDoctorId("");
            setSelectedTimeSlot("");
            setDoctorOpdCharge(0);
        }
    }, [doctors, selectedDoctorId]);

    const updateAppointmentMutation = useMutation({
        mutationFn: async (data: {
            scheduledAt: string;
            doctorId?: string;
            hospitalId?: string;
            visitType?: string;
            registrationSource?: string;
            registrationSourceDetails?: string;
        }) => {
            const appointmentId =
                activeAppointment.id ||
                (activeAppointment as { _id?: string; appointmentId?: string })._id ||
                (activeAppointment as { appointmentId?: string }).appointmentId;

            if (!appointmentId) {
                throw new Error('Appointment ID not found');
            }

            const response = await api.patch(
                `/api/appointment/update-appointment-schedule/${appointmentId}`,
                data
            );
            return response.data?.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['appointments'] });
            toast.success('Appointment updated successfully');
            onClose();
        },
        onError: (error: { response?: { data?: { message?: string } } }) => {
            const msg = error.response?.data?.message || 'Failed to update appointment';
            toast.error(
                msg.includes('fully booked')
                    ? 'That time slot is already full. Please pick another slot.'
                    : msg
            );
        },
    });

    const handleBookingHospitalChange = (hospitalId: string) => {
        setSelectedBookingHospitalId(hospitalId);
        setSelectedDoctorId("");
        setSelectedDate("");
        setSelectedTimeSlot("");
        setDoctorOpdCharge(0);
    };

    const handleDoctorChange = (doctorId: string) => {
        setSelectedDoctorId(doctorId);
        const doctor = doctors?.find((d) => d.id === doctorId);
        setDoctorOpdCharge(doctor?.opdCharge?.amount || 0);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (updateAppointmentMutation.isPending) {
            return;
        }

        if (needsHospitalPicker && !effectiveBookingHospitalId) {
            toast.error("Please select the hospital for this appointment.");
            return;
        }

        if (needsRegistrationSourceCapture) {
            if (!registrationSource.trim()) {
                toast.error("Please select how this self-registered patient heard about the hospital.");
                return;
            }
            if (
                leadSourceNeedsDetails(registrationSource) &&
                !(registrationSourceDetails || "").trim()
            ) {
                toast.error("Please enter registration source details.");
                return;
            }
        }

        const scheduledAtIso = istDateAndTimeToIso(selectedDate, selectedTimeSlot);
        if (!scheduledAtIso) {
            toast.error('Please select a valid date and time slot.');
            return;
        }

        if (!canBackdateOpd && visitType === 'OPD') {
            const todayIst = toIstDateInputValue(new Date());
            if (selectedDate < todayIst) {
                toast.error('Appointment date must be today or in the future');
                return;
            }
        }

        const updatePayload: {
            scheduledAt: string;
            doctorId?: string;
            hospitalId?: string;
            visitType?: string;
            registrationSource?: string;
            registrationSourceDetails?: string;
        } = {
            scheduledAt: scheduledAtIso,
            doctorId: selectedDoctorId,
        };

        if (effectiveBookingHospitalId) {
            updatePayload.hospitalId = effectiveBookingHospitalId;
        }

        const apiVisitType = formVisitTypeToApi(visitType);
        if (apiVisitType !== activeAppointment.visitType && !isFollowUp) {
            updatePayload.visitType = apiVisitType;
        }

        if (registrationSource.trim()) {
            updatePayload.registrationSource = registrationSource.trim();
            if ((registrationSourceDetails || "").trim()) {
                updatePayload.registrationSourceDetails = registrationSourceDetails.trim();
            }
        }

        await updateAppointmentMutation.mutateAsync(updatePayload);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto space-y-6">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold">Update Appointment</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <h3 className="font-medium text-gray-900">Patient Information</h3>
                        <p className="text-sm text-gray-600 mt-1">
                            {activeAppointment?.patient?.name} • {activeAppointment?.patient?.phone}
                            {activeAppointment.visitId ? (
                                <span className="block text-xs text-muted-foreground mt-1">
                                    Visit ID: {activeAppointment.visitId}
                                </span>
                            ) : null}
                        </p>
                    </div>

                    {hasClinicalLock && (
                        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                            This visit has billing or clinical notes. Hospital and visit type cannot be
                            changed; you can still update doctor, date, time, and registration source.
                        </p>
                    )}

                    {needsHospitalPicker && (
                        <div className="space-y-2">
                            <Label className="text-base font-semibold">Hospital</Label>
                            <Select
                                value={effectiveBookingHospitalId || undefined}
                                onValueChange={handleBookingHospitalChange}
                                disabled={hasClinicalLock || updateAppointmentMutation.isPending}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select hospital" />
                                </SelectTrigger>
                                <SelectContent>
                                    {bookingHospitals.map((h) => (
                                        <SelectItem key={h.id} value={h.id}>
                                            {h.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {selectedBookingHospital && (
                                <p className="text-xs text-muted-foreground">
                                    Doctors listed below are registered at {selectedBookingHospital.name}.
                                </p>
                            )}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-base font-semibold">Doctor</Label>
                            <DoctorSearchableSelect
                                doctors={doctors ?? []}
                                value={selectedDoctorId}
                                onValueChange={handleDoctorChange}
                                disabled={updateAppointmentMutation.isPending}
                                showOpdFee
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-base font-semibold">Visit Type</Label>
                            {isFollowUp ? (
                                <Input value="Follow-up" disabled className="bg-muted" />
                            ) : (
                                <Select
                                    value={visitType}
                                    onValueChange={(val) => setVisitType(val as FormVisitType)}
                                    disabled={hasClinicalLock || updateAppointmentMutation.isPending}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select visit type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="OPD">OPD</SelectItem>
                                        <SelectItem value="IPD">IPD</SelectItem>
                                        <SelectItem value="EMERGENCY">Emergency</SelectItem>
                                        <SelectItem value="DAY_CARE">Day Care</SelectItem>
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                    </div>

                    {visitType === 'OPD' && doctorOpdCharge > 0 && (
                        <p className="bg-green-50 text-green-700 p-2 rounded-md text-sm">
                            OPD Charge: ₹{doctorOpdCharge.toLocaleString("en-IN")}
                        </p>
                    )}

                    <div className="space-y-2">
                        <Label className="text-base font-semibold">
                            Registration Source{needsRegistrationSourceCapture ? " *" : ""}
                        </Label>
                        {needsRegistrationSourceCapture ? (
                            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                                This patient self-registered online. Please record how they heard about the
                                hospital.
                            </p>
                        ) : (
                            <p className="text-xs text-muted-foreground">
                                Defaults from the patient record. Change here if this visit differs.
                            </p>
                        )}
                        <LeadSourceSearchableSelect
                            value={registrationSource}
                            onValueChange={(val) => {
                                setRegistrationSource(val);
                                if (!leadSourceNeedsDetails(val)) {
                                    setRegistrationSourceDetails('');
                                }
                            }}
                            disabled={updateAppointmentMutation.isPending}
                            placeholder={
                                needsRegistrationSourceCapture
                                    ? "Select registration source"
                                    : "Keep existing (optional)"
                            }
                            searchPlaceholder="Search registration source…"
                        />
                        {leadSourceNeedsDetails(registrationSource) && (
                            <div className="space-y-2">
                                <Label className="text-sm text-muted-foreground">
                                    {registrationSource === 'REFERRAL'
                                        ? 'Referral Details'
                                        : registrationSource === 'AFFILIATE'
                                            ? 'Affiliate Details'
                                            : registrationSource === 'OTHER'
                                                ? 'Other Source'
                                                : 'Custom Source'}
                                </Label>
                                <Input
                                    value={registrationSourceDetails}
                                    onChange={(e) => setRegistrationSourceDetails(e.target.value)}
                                    placeholder={
                                        registrationSource === 'CUSTOM'
                                            ? 'Enter custom source'
                                            : 'Enter details'
                                    }
                                    disabled={updateAppointmentMutation.isPending}
                                />
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label className="text-base font-semibold">Appointment Date</Label>
                        <Input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            {...(canBackdateOpd
                                ? {}
                                : { min: toIstDateInputValue(new Date()) })}
                            required
                            disabled={updateAppointmentMutation.isPending}
                        />
                        {canBackdateOpd ? (
                            <p className="text-xs text-muted-foreground">
                                Chain admin: you can reschedule OPD visits to any date (past, today, or future).
                            </p>
                        ) : null}
                    </div>

                    {selectedDoctorId && selectedDate && (
                        <div className="space-y-2">
                            <Label className="text-base font-semibold">Select Time Slot</Label>
                            <DoctorSlots
                                doctorId={selectedDoctorId}
                                selectedDate={selectedDate}
                                hospitalId={effectiveBookingHospitalId || undefined}
                                excludeAppointmentId={activeAppointment.id}
                                onSlotSelect={(time) => {
                                    setSelectedTimeSlot(time);
                                }}
                            />
                            {selectedTimeSlot && (
                                <div className="text-sm text-green-700 bg-green-50 px-3 py-2 rounded-md border border-green-200">
                                    Selected Slot: <span className="font-semibold">{selectedTimeSlot}</span>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="bg-blue-800 text-white hover:bg-blue-700"
                            disabled={
                                !selectedDoctorId ||
                                !selectedDate ||
                                !selectedTimeSlot ||
                                updateAppointmentMutation.isPending
                            }
                        >
                            {updateAppointmentMutation.isPending ? 'Updating...' : 'Update Appointment'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
